/**
 * Main Navigation Links
 * Primary navigation items for the sidebar
 */

import { NAV_ICONS, BASE_PATHS } from "./constants";
import {
  createNavLink,
  createNavLinkWithChildren,
  createChildLinks,
} from "./linkBuilder";

/**
 * Dashboard Navigation
 */
const dashboardLink = createNavLink({
  title: "Dashboard",
  path: BASE_PATHS.DASHBOARD,
  icon: NAV_ICONS.LayoutDashboard,
});

/**
 * Agents Navigation
 */
const agentsLink = createNavLinkWithChildren({
  title: "Agents",
  path: BASE_PATHS.AGENTS,
  icon: NAV_ICONS.Users,
  children: createChildLinks([
    {
      title: "All Agents",
      path: BASE_PATHS.AGENTS,
      icon: NAV_ICONS.Users,
    },
    {
      title: "Registered Clients",
      path: `${BASE_PATHS.AGENTS}/registered-clients`,
      icon: NAV_ICONS.UserCheck,
    },
    {
      title: "Registered Students",
      path: `${BASE_PATHS.AGENTS}/registered-students`,
      icon: NAV_ICONS.GraduationCap,
    },
  ]),
});

/**
 * User Management Navigation
 */
const userManagementLink = createNavLinkWithChildren({
  title: "User Management",
  path: BASE_PATHS.USERS,
  icon: NAV_ICONS.Users,
  badge: "Soon",
  children: createChildLinks([
    {
      title: "All Users",
      path: `${BASE_PATHS.USERS}/all`,
      icon: NAV_ICONS.Users,
      badge: "Soon",
    },
    {
      title: "Active Users",
      path: `${BASE_PATHS.USERS}/active`,
      icon: NAV_ICONS.UserCheck,
      badge: "Soon",
    },
    {
      title: "Pending Verification",
      path: `${BASE_PATHS.USERS}/pending`,
      icon: NAV_ICONS.Clock,
      badge: "Soon",
    },
  ]),
});

/**
 * Insurance Navigation
 */
const insuranceLink = createNavLinkWithChildren({
  title: "Insurance",
  path: BASE_PATHS.INSURANCE,
  icon: NAV_ICONS.Shield,
  children: createChildLinks([
    {
      title: "Policies",
      path: `${BASE_PATHS.INSURANCE}-policies`,
      icon: NAV_ICONS.FileText,
    },
    {
      title: "Claims",
      path: `${BASE_PATHS.INSURANCE}/claims`,
      icon: NAV_ICONS.AlertTriangle,
    },
    {
      title: "Approved Claims",
      path: `${BASE_PATHS.INSURANCE}/approved`,
      icon: NAV_ICONS.CheckCircle,
    },
    {
      title: "Rejected Claims",
      path: `${BASE_PATHS.INSURANCE}/rejected`,
      icon: NAV_ICONS.XCircle,
    },
  ]),
});

/**
 * Providers Navigation
 */
const providersLink = createNavLink({
  title: "Providers",
  path: BASE_PATHS.PROVIDERS,
  icon: NAV_ICONS.Building2,
});

/**
 * Purchases Navigation
 */
const purchasesLink = createNavLink({
  title: "Purchases",
  path: BASE_PATHS.PURCHASES,
  icon: NAV_ICONS.CreditCard,
});

/**
 * Payments Navigation (kept for backward compatibility)
 */
const paymentsLink = createNavLinkWithChildren({
  title: "Payments",
  path: BASE_PATHS.PAYMENTS,
  icon: NAV_ICONS.CreditCard,
  children: createChildLinks([
    {
      title: "Transactions",
      path: `${BASE_PATHS.PAYMENTS}/transactions`,
      icon: NAV_ICONS.CreditCard,
    },
    {
      title: "Pending Payments",
      path: `${BASE_PATHS.PAYMENTS}/pending`,
      icon: NAV_ICONS.Clock,
    },
    {
      title: "Failed Payments",
      path: `${BASE_PATHS.PAYMENTS}/failed`,
      icon: NAV_ICONS.XCircle,
    },
  ]),
});

/**
 * Analytics Navigation
 */
const analyticsLink = createNavLinkWithChildren({
  title: "Analytics",
  path: BASE_PATHS.ANALYTICS,
  icon: NAV_ICONS.BarChart3,
  badge: "Soon",
  children: createChildLinks([
    {
      title: "Overview",
      path: `${BASE_PATHS.ANALYTICS}/overview`,
      icon: NAV_ICONS.TrendingUp,
      badge: "Soon",
    },
    {
      title: "Revenue",
      path: `${BASE_PATHS.ANALYTICS}/revenue`,
      icon: NAV_ICONS.BarChart3,
      badge: "Soon",
    },
    {
      title: "User Growth",
      path: `${BASE_PATHS.ANALYTICS}/users`,
      icon: NAV_ICONS.Users,
      badge: "Soon",
    },
  ]),
});

/**
 * Reports Navigation
 */
const reportsLink = createNavLinkWithChildren({
  title: "Reports",
  path: BASE_PATHS.REPORTS,
  icon: NAV_ICONS.FileText,
  badge: "Soon",
  children: createChildLinks([
    {
      title: "Monthly Reports",
      path: `${BASE_PATHS.REPORTS}/monthly`,
      icon: NAV_ICONS.Calendar,
      badge: "Soon",
    },
    {
      title: "Insurance Reports",
      path: `${BASE_PATHS.REPORTS}/insurance`,
      icon: NAV_ICONS.Shield,
      badge: "Soon",
    },
    {
      title: "Payment Reports",
      path: `${BASE_PATHS.REPORTS}/payments`,
      icon: NAV_ICONS.CreditCard,
      badge: "Soon",
    },
  ]),
});

// Media Navigation - Individual Links (moved from Media Library dropdown)
const organizerManagementLink = createNavLink({
  title: "Organizer Management",
  path: `${BASE_PATHS.MEDIA}/organizer`,
  icon: NAV_ICONS.FileText,
});

const eventsLink = createNavLink({
  title: "Events",
  path: `${BASE_PATHS.MEDIA}/events`,
  icon: NAV_ICONS.Calendar,
});

// const toursLink = createNavLink({
//   title: "Tours",
//   path: `${BASE_PATHS.MEDIA}/tours`,
//   icon: NAV_ICONS.MapPin,
// });

const conferencesLink = createNavLink({
  title: "Conferences",
  path: `${BASE_PATHS.MEDIA}/conferences`,
  icon: NAV_ICONS.GraduationCap,
});

const tradeMissionsLink = createNavLink({
  title: "Trade Missions",
  path: `${BASE_PATHS.MEDIA}/trade-missions`,
  icon: NAV_ICONS.Briefcase,
});

// const teamsLink = createNavLink({
//   title: "Teams",
//   path: `${BASE_PATHS.MEDIA}/teams`,
//   icon: NAV_ICONS.Newspaper,
// });

/**
 * Help & Support Navigation
 */
const helpLink = createNavLinkWithChildren({
  title: "Help & Support",
  path: BASE_PATHS.HELP,
  icon: NAV_ICONS.HelpCircle,
  badge: "Soon",
  children: createChildLinks([
    {
      title: "Contact Support",
      path: `${BASE_PATHS.HELP}/contact`,
      icon: NAV_ICONS.Bell,
      badge: "Soon",
    },
  ]),
});

/**
 * Main Navigation Links Array
 * Add new links here or import from separate files for better organization
 */
export const mainNavLinks = [
  dashboardLink,
  agentsLink,
  // Coming Soon - Commented out for now
  // userManagementLink,
  // insuranceLink,
  // providersLink,
  // paymentsLink,
  // analyticsLink,
  // reportsLink,
  // helpLink,
];

/**
 * Export individual links for granular imports if needed
 */
export {
  dashboardLink,
  agentsLink,
  userManagementLink,
  insuranceLink,
  providersLink,
  purchasesLink,
  paymentsLink,
  analyticsLink,
  reportsLink,
  helpLink,
  organizerManagementLink,
  eventsLink,
  // toursLink,
  conferencesLink,
  tradeMissionsLink,
  // teamsLink,
};
